import React, { useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Camera, Upload, X, Sparkles, Loader2, Lock, AlertCircle } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PHOTOS = 5;
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_BYTES = 8 * 1024 * 1024;

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(r.error);
    r.onload = () => resolve(r.result);
    r.readAsDataURL(file);
  });

const fmt = (n) =>
  Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/**
 * AI Photo Estimator — Pro-only.
 * Compact mode (compact=true) shows just the upload zone + a button.
 * On success, fires onApply(estimate) so the parent can prefill the quote builder.
 */
const PhotoEstimator = ({ onApply, compact = false, defaultProjectType = '' }) => {
  const fileInput = useRef(null);
  const cameraInput = useRef(null);
  const { user } = useAuth();
  const { toast } = useToast();

  const [photos, setPhotos] = useState([]); // [{file, dataUrl, mime}]
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [error, setError] = useState('');

  const isPro = user?.subscription_status === 'pro';

  const handleFiles = async (filesList) => {
    const files = Array.from(filesList || []);
    if (!files.length) return;

    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) {
      toast({
        title: 'Photo limit reached',
        description: `You can upload up to ${MAX_PHOTOS} photos per estimate.`,
        variant: 'destructive',
      });
      return;
    }

    const valid = [];
    for (const file of files.slice(0, remaining)) {
      if (!ACCEPTED.includes(file.type)) {
        toast({
          title: 'Unsupported format',
          description: `${file.name}: only JPEG, PNG, or WEBP.`,
          variant: 'destructive',
        });
        continue;
      }
      if (file.size > MAX_BYTES) {
        toast({
          title: 'Image too large',
          description: `${file.name}: max 8 MB.`,
          variant: 'destructive',
        });
        continue;
      }
      try {
        const dataUrl = await fileToDataUrl(file);
        valid.push({ file, dataUrl, mime: file.type });
      } catch {
        // skip
      }
    }
    if (valid.length) setPhotos((p) => [...p, ...valid]);
  };

  const removePhoto = (idx) => setPhotos((p) => p.filter((_, i) => i !== idx));

  const runEstimate = async () => {
    if (!isPro) {
      toast({
        title: 'Pro feature',
        description: 'Upgrade to Pro to unlock AI Photo Estimation.',
        variant: 'destructive',
      });
      return;
    }
    if (photos.length === 0) {
      setError('Add at least one photo.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/ai/photo-estimate`,
        {
          photos: photos.map((p) => ({ data_url: p.dataUrl })),
          project_type_hint: defaultProjectType || null,
          notes: notes || null,
        },
        { withCredentials: true, timeout: 90000 }
      );
      setEstimate(data);
      toast({
        title: 'AI estimate ready',
        description: `${data.project_name} • ${fmt(data.suggested_total)}`,
      });
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Estimate failed';
      setError(msg);
      toast({ title: 'Estimate failed', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const applyToQuote = () => {
    if (estimate && onApply) onApply(estimate, photos);
    toast({
      title: 'Applied to quote builder',
      description: photos.length
        ? `Quote builder filled. ${photos.length} photo${photos.length > 1 ? 's' : ''} attached for the PDF.`
        : 'Review and tweak the values, then send your quote.',
    });
  };

  return (
    <Card className={compact ? 'border border-dashed border-blue-300 bg-blue-50/40' : 'border-2'} data-testid="photo-estimator">
      <CardHeader className={compact ? 'py-3' : ''}>
        <CardTitle className="flex items-center gap-2 text-slate-900">
          <Sparkles className="w-5 h-5 text-blue-600" />
          AI Photo Estimator
          {!isPro && (
            <Badge variant="outline" className="ml-auto text-amber-700 border-amber-300 bg-amber-50">
              <Lock className="w-3 h-3 mr-1" /> Pro
            </Badge>
          )}
        </CardTitle>
        {!compact && (
          <p className="text-xs text-slate-500">
            Upload up to {MAX_PHOTOS} photos. AI suggests scope, materials, and pricing in seconds.
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Upload zone */}
        <div
          onClick={() => isPro && fileInput.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
          }}
          onDrop={(e) => {
            e.preventDefault();
            if (isPro) handleFiles(e.dataTransfer.files);
          }}
          className={`rounded-lg border-2 border-dashed text-center py-6 px-4 transition ${
            isPro
              ? 'border-blue-300 hover:border-blue-500 hover:bg-blue-50/50 cursor-pointer'
              : 'border-slate-200 bg-slate-50 cursor-not-allowed opacity-60'
          }`}
          data-testid="photo-estimator-dropzone"
        >
          <Upload className="w-7 h-7 mx-auto text-blue-600 mb-2" />
          <p className="text-sm text-slate-700 font-medium">
            {isPro ? 'Drop photos or tap to upload' : 'Pro feature — upgrade to unlock'}
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            JPEG / PNG / WEBP • up to 8 MB each • {photos.length}/{MAX_PHOTOS} used
          </p>
          {isPro && (
            <div className="mt-3 flex flex-col sm:flex-row gap-2 justify-center">
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={(e) => { e.stopPropagation(); cameraInput.current?.click(); }}
                className="min-h-[44px] gap-2"
                data-testid="photo-estimator-camera-btn"
              >
                <Camera className="w-4 h-4" /> Take photo
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={(e) => { e.stopPropagation(); fileInput.current?.click(); }}
                className="min-h-[44px] gap-2"
                data-testid="photo-estimator-gallery-btn"
              >
                <Upload className="w-4 h-4" /> Pick from gallery
              </Button>
            </div>
          )}
          <input
            ref={fileInput}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="photo-estimator-file-input"
          />
          <input
            ref={cameraInput}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="photo-estimator-camera-input"
          />
        </div>

        {/* Thumbnails */}
        {photos.length > 0 && (
          <div className="grid grid-cols-5 gap-2">
            {photos.map((p, i) => (
              <div key={i} className="relative group rounded-md overflow-hidden border border-slate-200">
                <img src={p.dataUrl} alt={`photo-${i}`} className="aspect-square object-cover w-full" />
                <button
                  type="button"
                  onClick={() => removePhoto(i)}
                  className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                  data-testid={`photo-estimator-remove-${i}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Notes */}
        {!compact && isPro && (
          <Textarea
            placeholder="Optional notes for the AI (e.g., 'two-coat exterior, prep needed')"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            data-testid="photo-estimator-notes"
          />
        )}

        {error && (
          <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="photo-estimator-error">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <Button
          onClick={runEstimate}
          disabled={loading || !isPro || photos.length === 0}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white"
          data-testid="photo-estimator-run-btn"
        >
          {loading ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing photos…</>
          ) : (
            <><Camera className="w-4 h-4 mr-2" /> Estimate from photos</>
          )}
        </Button>

        {/* Result */}
        {estimate && (
          <div className="rounded-lg border-2 border-green-200 bg-green-50 p-4 space-y-3" data-testid="photo-estimator-result">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-green-700 font-semibold uppercase tracking-wide">AI Estimate</p>
                <h4 className="text-lg font-bold text-slate-900" data-testid="photo-estimator-result-name">
                  {estimate.project_name}
                </h4>
              </div>
              <Badge className="bg-green-600 hover:bg-green-600 text-white">
                {Math.round(estimate.confidence * 100)}% confidence
              </Badge>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-white rounded p-2">
                <p className="text-xs text-slate-500">Suggested total</p>
                <p className="text-xl font-bold text-blue-700" data-testid="photo-estimator-result-total">
                  {fmt(estimate.suggested_total)}
                </p>
              </div>
              <div className="bg-white rounded p-2">
                <p className="text-xs text-slate-500">{estimate.measurement_type.replace('_', ' ')}</p>
                <p className="text-xl font-bold text-slate-900">{estimate.measurement_value}</p>
              </div>
            </div>
            {estimate.scope_items?.length > 0 && (
              <ul className="text-xs text-slate-700 list-disc list-inside space-y-0.5">
                {estimate.scope_items.slice(0, 5).map((it, i) => (
                  <li key={i}>
                    {it.label}
                    {it.estimated_cost != null ? ` — ${fmt(it.estimated_cost)}` : ''}
                  </li>
                ))}
              </ul>
            )}
            {estimate.ai_notes && (
              <p className="text-xs text-slate-600 italic">{estimate.ai_notes}</p>
            )}
            {onApply && (
              <Button
                onClick={applyToQuote}
                size="sm"
                className="w-full bg-green-600 hover:bg-green-700 text-white"
                data-testid="photo-estimator-apply-btn"
              >
                Apply to quote builder
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PhotoEstimator;
